import { useEffect } from 'react';
import { handleBridgeMessage, READY_MESSAGE } from '../utils/logoutSync';

// Invisible cross-origin logout bridge (no ticket — follow-up to CC-602).
//
// A sibling product (Crime Central or Platform) loads this route in a hidden
// iframe and postMessages it when the user logs out there. We validate the
// sender and, if legitimate, broadcast to this origin's open tabs and run a
// best-effort local revoke. Renders nothing — it exists only to receive the
// message. See utils/logoutSync.js for the validation + handling logic.
export default function LogoutBridge() {
  useEffect(() => {
    // Order matters: attach the listener before announcing readiness, so a
    // logout message that arrives the instant the parent sees the ack can't
    // itself be dropped. Previously the sender posted its one-shot logout
    // message on the iframe's `load` event, which fires before this effect
    // ever runs — the message was always lost. Now the parent waits for this
    // ready ack before sending anything (see fanOutCrossOriginLogout).
    //
    // The sender's retry fallback and its ready-triggered send race each
    // other on THIS side too: if the ready ack crosses paths with an
    // in-flight blind retry, both a retry-post and the ready-post can arrive
    // here. This bridge instance exists to service exactly one cross-origin
    // logout for its short lifetime, so latch on the first valid message and
    // ignore anything after — otherwise broadcastLogout/serverRevokeAndClear
    // (and their network calls) would run twice for one logout.
    let handled = false;
    const onMessage = (event) => {
      if (handled) return;
      if (handleBridgeMessage(event)) handled = true;
    };
    window.addEventListener('message', onMessage);

    // '*' is intentional here only: this ack carries no sensitive data, and
    // the bridge can't reliably know its parent's origin from inside the
    // iframe.
    window.parent.postMessage(READY_MESSAGE, '*');

    return () => window.removeEventListener('message', onMessage);
  }, []);

  return null;
}
